import type { Dispatch, SetStateAction } from 'react';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { isEqual } from 'lodash';
import { SORT_ASC, SORT_DESC } from 'pages/Valuations/util/constants';
import { getStringValue } from 'utilities';
import {
  GPCConfigurationSpreadsheets,
  GPCExtended,
  GPCTableData,
  ValuationApproachGPCExtended,
} from '../GuidelinePublicCompanies.props';
import type { CapitalIqExtraInfoSortKey } from '../utils/gpcCapitalIqExtraInfoSort';
import {
  getCapitalIqExtraInfoSortFromLastSortedColumn,
  getEffectiveCapitalIqSortedColumnInput,
  resolveCapitalIqExtraInfoSort,
  sortGpcComparisonsByCapitalIqColumn,
} from '../utils/gpcCapitalIqExtraInfoSort';

/** Below this count there is no row order to reconcile (sorting is a no-op). */
const MIN_PUBLIC_COMPS_FOR_ORDER_SYNC = 2;

/** Stable fallback so `useMemo` / referential checks are not invalidated by `?? []` allocating every render. */
const EMPTY_GPC_COMPARISON_LIST: GPCExtended[] = [];

/** Stable identity for the current set of public comps (order-independent). */
const sortedPublicCompCapIqIds = (comparisons: GPCExtended[]): string[] =>
  comparisons
    .map(c => String(c.cap_iq_id ?? ''))
    .filter(id => id.length > 0)
    .sort((a, b) => a.localeCompare(b));

/**
 * Primitive dependency for `useLayoutEffect`: public-comp *membership* only (order ignored).
 * `gpcSpreadsheet` can keep the same reference while `tableData` is mutated, so this key must
 * change when comps are added or removed. `JSON.stringify` avoids delimiter collisions in ids.
 */
const publicCompCapIqSetKey = (comparisons: GPCExtended[]): string =>
  JSON.stringify(sortedPublicCompCapIqIds(comparisons));

export type UseSyncGpcPublicCompRowOrderParams = {
  approachPanelId: string | number | undefined;
  gpcSpreadsheet: GPCConfigurationSpreadsheets;
  lastSortedColumn: Record<string, string> | null;
  /** Intentionally `unknown` so assembled approach objects match `GuidelinePublicCompanies`’s `updateApproach` without duplicating API typings. */
  updateApproach: (
    approachGpc: unknown,
    tableData: GPCTableData,
    updatedSortedColumn?: Record<string, string>
  ) => void;
  setLastSortedColumn: Dispatch<SetStateAction<Record<string, string> | null>>;
};

export type UseSyncGpcPublicCompRowOrderResult = {
  /** Reorders comparisons to match the active Capital IQ Extra Info sort (state + ref fallback); updates ref and sometimes `lastSortedColumn`. */
  orderGpcComparisonsForAdd: (comparisons: GPCExtended[]) => {
    orderedComparisons: GPCExtended[];
    sortedColumnForUi: Record<string, string>;
  };
  /** Header-click sort for Capital IQ Extra Info columns; shared ordering with add + layout sync. */
  applyCapitalIqExtraInfoHeaderSort: (key: CapitalIqExtraInfoSortKey) => void;
};

/**
 * Keeps `gpc_comparison` row order aligned with persisted `order` after load. For unsaved rows, applies
 * Capital IQ Extra Info sorting from `lastSortedColumn`, or market cap descending when no Capital IQ Extra Info sort is set yet.
 * Co-located here so edits to spreadsheet wiring in `GuidelinePublicCompanies` do not drop this behavior.
 */
const useSyncGpcPublicCompRowOrder = ({
  approachPanelId,
  gpcSpreadsheet,
  lastSortedColumn,
  updateApproach,
  setLastSortedColumn,
}: UseSyncGpcPublicCompRowOrderParams): UseSyncGpcPublicCompRowOrderResult => {
  /** Mirrors the last Capital IQ Extra Info column sort so add + layout sync can use it if React state lags. */
  const lastAppliedCapitalIqSortedColumnRef = useRef<Record<string, string> | null>(null);

  useEffect(() => {
    const active = getCapitalIqExtraInfoSortFromLastSortedColumn(lastSortedColumn);
    if (active != null) {
      lastAppliedCapitalIqSortedColumnRef.current = active.sortedColumnForUi;
    }
  }, [lastSortedColumn]);

  // New GPC panel: clear stored Capital IQ Extra Info sort so it is not reused across panels.
  useEffect(() => {
    lastAppliedCapitalIqSortedColumnRef.current = null;
  }, [approachPanelId]);

  const comparisons =
    gpcSpreadsheet.tableData.approach.valuations_approach_gpc?.gpc_comparison ?? EMPTY_GPC_COMPARISON_LIST;

  // Avoid re-sort + stringify on every render when `gpc_comparison` keeps the same reference (typical with
  // in-place `tableData` updates). Effect deps still compare the resulting string by value.
  const publicCompMembershipKey = useMemo(() => publicCompCapIqSetKey(comparisons), [comparisons]);

  const lastSyncedPublicCompListRef = useRef<{
    approachPanelId: UseSyncGpcPublicCompRowOrderParams['approachPanelId'];
    sortedCapIqIds: string[];
    capitalIqSortSignature: string;
  } | null>(null);

  useLayoutEffect(() => {
    const effectiveSortedColumn = getEffectiveCapitalIqSortedColumnInput(
      lastSortedColumn,
      lastAppliedCapitalIqSortedColumnRef.current
    );
    const capitalIqSortSignature = JSON.stringify(
      resolveCapitalIqExtraInfoSort(effectiveSortedColumn).sortedColumnForUi
    );

    const list =
      gpcSpreadsheet.tableData.approach.valuations_approach_gpc?.gpc_comparison ?? EMPTY_GPC_COMPARISON_LIST;
    const sortedIds = sortedPublicCompCapIqIds(list);
    const prev = lastSyncedPublicCompListRef.current;
    if (
      prev?.approachPanelId === approachPanelId &&
      isEqual(prev?.sortedCapIqIds, sortedIds) &&
      prev?.capitalIqSortSignature === capitalIqSortSignature
    ) {
      return;
    }
    lastSyncedPublicCompListRef.current = { approachPanelId, sortedCapIqIds: sortedIds, capitalIqSortSignature };

    if (list.length < MIN_PUBLIC_COMPS_FOR_ORDER_SYNC) {
      return;
    }

    const hasPersistedComparisons = list.some(c => c.id != null);
    if (hasPersistedComparisons) {
      const orderedBySavedOrder = [...list].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      const arrayOrderMatchesSavedOrder = orderedBySavedOrder.every(
        (row, index) => row.cap_iq_id === list[index].cap_iq_id
      );
      if (!arrayOrderMatchesSavedOrder) {
        const { tableData } = gpcSpreadsheet;
        const valuationApproachGPC = {
          ...tableData.approach.valuations_approach_gpc,
          gpc_comparison: orderedBySavedOrder,
        };
        tableData.approach.valuations_approach_gpc = valuationApproachGPC as ValuationApproachGPCExtended;
        updateApproach(valuationApproachGPC, tableData, {});
      }
      return;
    }

    const resolvedSort = resolveCapitalIqExtraInfoSort(effectiveSortedColumn);
    const { sortKey, sortDescending, sortedColumnForUi } = resolvedSort;
    const sortedList = sortGpcComparisonsByCapitalIqColumn(list, sortKey, sortDescending);
    const alreadyMatchesSort = sortedList.every((row, index) => row.cap_iq_id === list[index].cap_iq_id);

    setLastSortedColumn(sortedColumnForUi);

    if (alreadyMatchesSort) {
      return;
    }

    const { tableData } = gpcSpreadsheet;
    const valuationApproachGPC = {
      ...tableData.approach.valuations_approach_gpc,
      gpc_comparison: sortedList.map((comp, index) => ({
        ...comp,
        order: index,
      })),
    };
    tableData.approach.valuations_approach_gpc = valuationApproachGPC as ValuationApproachGPCExtended;
    updateApproach(valuationApproachGPC, tableData, sortedColumnForUi);
  }, [
    approachPanelId,
    lastSortedColumn,
    publicCompMembershipKey,
    gpcSpreadsheet,
    updateApproach,
    setLastSortedColumn,
  ]);

  const orderGpcComparisonsForAdd = useCallback(
    (comparisonList: GPCExtended[]) => {
      const effectiveSortedColumn = getEffectiveCapitalIqSortedColumnInput(
        lastSortedColumn,
        lastAppliedCapitalIqSortedColumnRef.current
      );
      const { sortKey, sortDescending, sortedColumnForUi } = resolveCapitalIqExtraInfoSort(effectiveSortedColumn);
      const orderedComparisons = sortGpcComparisonsByCapitalIqColumn(comparisonList, sortKey, sortDescending);
      lastAppliedCapitalIqSortedColumnRef.current = sortedColumnForUi;
      if (
        getCapitalIqExtraInfoSortFromLastSortedColumn(lastSortedColumn) == null &&
        effectiveSortedColumn != null
      ) {
        setLastSortedColumn(sortedColumnForUi);
      }
      return { orderedComparisons, sortedColumnForUi };
    },
    [lastSortedColumn, setLastSortedColumn]
  );

  const applyCapitalIqExtraInfoHeaderSort = useCallback(
    (key: CapitalIqExtraInfoSortKey) => {
      const { tableData } = gpcSpreadsheet;
      const currentSortedColumn = getStringValue(lastSortedColumn?.[key]);
      const currentSortedColumnIsDesc = currentSortedColumn === SORT_DESC;
      const updatedSortedColumn = {
        [key]: currentSortedColumnIsDesc ? SORT_ASC : SORT_DESC,
      };
      const sortedComparison = sortGpcComparisonsByCapitalIqColumn(
        tableData.approach.valuations_approach_gpc?.gpc_comparison ?? EMPTY_GPC_COMPARISON_LIST,
        key,
        !currentSortedColumnIsDesc
      );
      setLastSortedColumn(updatedSortedColumn);
      const valuationApproachGPC = {
        ...tableData.approach.valuations_approach_gpc,
        gpc_comparison: sortedComparison.map((comp, index) => ({
          ...comp,
          order: index,
        })),
      };
      tableData.approach.valuations_approach_gpc = valuationApproachGPC as ValuationApproachGPCExtended;
      updateApproach(valuationApproachGPC, tableData, updatedSortedColumn);
    },
    [gpcSpreadsheet, lastSortedColumn, setLastSortedColumn, updateApproach]
  );

  return { orderGpcComparisonsForAdd, applyCapitalIqExtraInfoHeaderSort };
};

export default useSyncGpcPublicCompRowOrder;
